/**
 * Persists the chat answer-model tier in localStorage.
 *
 * Tier names (current):
 *   standard   - lighter / faster model
 *   heavy_duty - balanced model (default); currently the newest flash
 *   pro        - strongest reasoning model (new, repurposed from legacy alias)
 *   gemini-3.7-flash - pinned to exactly that model id
 *
 * The backend is the source of truth for which tiers exist
 * (GET /api/v1/models/tiers), but this store ALSO gates on VALID_TIERS below:
 * setModelTier() rejects anything absent from that list. A tier added to
 * model_tiers.json therefore needs a matching entry here, or the picker will
 * appear to select it while getModelTier() keeps returning the old tier and
 * chat keeps sending the old model. Legacy "fast" still aliases to "standard". "pro" used to alias to
 * "heavy_duty" but now names its own tier — old localStorage values get
 * the new pro model, a strict upgrade. "latest_flash" now aliases to
 * "heavy_duty": heavy_duty became the newest flash, so the two resolved to the
 * same model and the picker listed it twice. Panes holding "latest_flash" in
 * localStorage still resolve, and still land on the newest flash.
 *
 * Default tier when key is missing or invalid: "heavy_duty".
 *
 * Falls back to an in-memory variable if localStorage is unavailable
 * (private browsing, quota, sandboxed iframe).
 */

const STORAGE_KEY = "shibumi.modelTier";
const VALID_TIERS = ["standard", "heavy_duty", "pro", "gemini-3.7-flash"];
const DEFAULT_TIER = "heavy_duty";

// Legacy tier name -> canonical.
// "pro" is no longer aliased — it's a real tier now.
const ALIASES = { fast: "standard", latest_flash: "heavy_duty" };

let inMemoryTier = DEFAULT_TIER;

function canonicalize(tier) {
  if (!tier) return null;
  return ALIASES[tier] || tier;
}

function isValid(tier) {
  return VALID_TIERS.indexOf(tier) !== -1;
}

function safeRead() {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch (_err) {
    return null;
  }
}

function safeWrite(tier) {
  try {
    window.localStorage.setItem(STORAGE_KEY, tier);
    return true;
  } catch (_err) {
    return false;
  }
}

export function getModelTier() {
  const stored = safeRead();
  const canon = canonicalize(stored);

  if (canon && isValid(canon)) {
    // Migrate legacy stored value if needed.
    if (stored !== canon) {
      safeWrite(canon);
    }
    inMemoryTier = canon;
    return canon;
  }

  if (stored && !isValid(canon)) {
    safeWrite(DEFAULT_TIER);
    inMemoryTier = DEFAULT_TIER;
    return DEFAULT_TIER;
  }

  return isValid(inMemoryTier) ? inMemoryTier : DEFAULT_TIER;
}

/**
 * Persist the chat answer-model tier and return whether durable
 * storage was actually updated.
 *
 * Returns:
 *   - false if `tier` is not a valid value (no state changes).
 *   - true if `tier` was both accepted in memory AND persisted to localStorage.
 *   - false if `tier` was accepted in memory but localStorage was unavailable
 *     (the in-memory copy still survives for this page lifetime - subsequent
 *     getModelTier() calls in this page return the new tier).
 *
 * Legacy aliases ("fast" / "pro") are normalised before storage.
 */
export function setModelTier(tier) {
  const canon = canonicalize(tier);
  if (!isValid(canon)) return false;
  inMemoryTier = canon;
  return safeWrite(canon);
}

export const MODEL_TIER_STORAGE_KEY = STORAGE_KEY;
export const VALID_MODEL_TIERS = VALID_TIERS;
